import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Injectable, Optional } from "@angular/core";
import { environment } from "@app/../environments/environment";
import { ChannelType, HeaderKey } from "@shared/clickar-constants";
import { AuctionDTO, AuctionFullDTO, CreateAuctionDTO, ObservedAuctionDTO, OfferResponseDTO, PortalInfoDTO } from "@shared/model/auction.model";
import { FeedbackDTO } from "@shared/model/auctioneer.model";
import { LotDetailDTO, LotReportDTO, Purchase } from "@shared/model/lotDTO";
import { VehicleDTO } from "@shared/model/vehicle.model";
import { Configuration } from "@shared/services/utils/configuration";
import { CustomHttpUrlEncodingCodec } from "@shared/services/utils/encoder";
import { Observable } from "rxjs";

@Injectable()
export class AuctionService {
	protected basePath = "https://localhost:8088";
    protected k8sPath = 'https://localhost:8088';

    defaultHeaders = new HttpHeaders();
	configuration = new Configuration();
	readonly encoderParameters = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });

	constructor(protected httpClient: HttpClient, @Optional() configuration: Configuration) {
		this.basePath = environment.baseApiUrl;
        this.k8sPath = environment.k8sApiUrl;


        if (configuration) {
			this.configuration = configuration;
			this.basePath = environment.baseApiUrl || configuration.basePath || this.basePath;
		}
	}

	getAuctionByIdTemplatePageableUsingGET(idTemplate: number, limit?: number, offset?: number, sortField?: string, sortOrder?: string, observe?: "body", reportProgress?: boolean): Observable<AuctionDTO[]> {
		let queryParameters = this.encoderParameters;
		if (limit !== undefined && limit !== null) {
			queryParameters = queryParameters.set("limit", <any>limit);
		}
		if (offset !== undefined && offset !== null) {
			queryParameters = queryParameters.set("offset", <any>offset);
		}
		if (sortField !== undefined && sortField !== null) {
			queryParameters = queryParameters.set("sortField", <any>sortField);
		}
		if (sortOrder !== undefined && sortOrder !== null) {
			queryParameters = queryParameters.set("sortOrder", <any>sortOrder);
		}

		return this.httpClient.get<AuctionDTO[]>(`${this.basePath}/api/v2/secure/auction/${encodeURIComponent(String(idTemplate))}`,
			{
				params: queryParameters,
				withCredentials: this.configuration.withCredentials,
				headers: this.defaultHeaders,
				observe,
				reportProgress
			}
		);
	}

	createAuctionUsingPOST = (auctionDTO: CreateAuctionDTO, observe?: "body", reportProgress?: boolean): Observable<any> =>
		this.httpClient.post<any>(`${this.basePath}/api/v1/secure/auction`,
			auctionDTO,
			{
				withCredentials: this.configuration.withCredentials,
				headers: this.defaultHeaders,
				observe,
				reportProgress
			}
		);

	getAuctionFullSearchUsingGET(idAuction: number, brand?: string, licensePlate?: string, model?: string, region?: string, state?: string, idVehicle?: number, frame?: string, observe?: "body", reportProgress?: boolean): Observable<AuctionFullDTO> {
		let queryParameters = this.encoderParameters;
		if (brand !== undefined && brand !== null) {
			queryParameters = queryParameters.set("brand", <any>brand);
		}
		if (licensePlate !== undefined && licensePlate !== null) {
			queryParameters = queryParameters.set("licensePlate", <any>licensePlate);
		}
		if (model !== undefined && model !== null) {
			queryParameters = queryParameters.set("model", <any>model);
		}
		if (region !== undefined && region !== null) {
			queryParameters = queryParameters.set("region", <any>region);
		}
		if (state !== undefined && state !== null) {
			queryParameters = queryParameters.set("state", <any>state);
		}
		if (idVehicle !== undefined && idVehicle !== null) {
			queryParameters = queryParameters.set("idVehicle", <any>idVehicle);
		}
		if (frame !== undefined && frame !== null) {
			queryParameters = queryParameters.set("frame", <any>frame);
		}

		return this.httpClient.get<AuctionFullDTO>(`${this.basePath}/api/v1/secure/auctionFull/${encodeURIComponent(String(idAuction))}/search`,
			{
				params: queryParameters,
				withCredentials: this.configuration.withCredentials,
				headers: this.defaultHeaders,
				observe,
				reportProgress
			}
		);
	}

	awardLotUsingPATCH(idAuction: number, idAuctionOffer: number, idLot: number, observe?: "body", reportProgress?: boolean): Observable<any> {
		let headers = this.defaultHeaders;

		const httpHeaderAccepts: string[] = ["*/*"],
			httpHeaderAcceptSelected: string | undefined = this.configuration.selectHeaderAccept(httpHeaderAccepts);
		if (httpHeaderAcceptSelected != undefined) {
			headers = headers.set("Accept", httpHeaderAcceptSelected)
				.set(HeaderKey.NO_SUCCESS_POPUP, "true");
		}

		return this.httpClient.patch<any>(`${this.basePath}/api/v1/secure/auction/${encodeURIComponent(String(idAuction))}/award/${encodeURIComponent(String(idLot))}/${encodeURIComponent(String(idAuctionOffer))}`,
			null,
			{
				withCredentials: this.configuration.withCredentials,
				headers,
				observe,
				reportProgress
			}
		);
	}

	searchInRunningAndScheduledAuctionsUsingGET(toSearch: string, observe?: "body", reportProgress?: boolean): Observable<AuctionDTO[]> {
		let queryParameters = this.encoderParameters;
		if (toSearch !== undefined && toSearch !== null) {
			queryParameters = queryParameters.set("toSearch", <any>toSearch);
		}

		return this.httpClient.get<AuctionDTO[]>(`${this.basePath}/api/v1/secure/running-auctions/search`,
			{
				params: queryParameters,
				withCredentials: this.configuration.withCredentials,
				headers: this.defaultHeaders,
				observe,
				reportProgress
			}
		);
	}

	editFileMessageUsingPATCH(idAuction: number, idFileMessage: number, observe?: "body", reportProgress?: boolean): Observable<any> {
		let headers = this.defaultHeaders;

		const httpHeaderAccepts: string[] = ["*/*"],
			httpHeaderAcceptSelected: string | undefined = this.configuration.selectHeaderAccept(httpHeaderAccepts);
		if (httpHeaderAcceptSelected != undefined) {
			headers = headers.set("Accept", httpHeaderAcceptSelected)
				.set(HeaderKey.NO_SUCCESS_POPUP, "true");
		}

		// to determine the Content-Type header
		const consumes: string[] = ["application/json"],
			httpContentTypeSelected: string | undefined = this.configuration.selectHeaderContentType(consumes);
		if (httpContentTypeSelected != undefined) {
			headers = headers.set("Content-Type", httpContentTypeSelected);
		}

		return this.httpClient.patch<any>(`${this.basePath}/api/v1/secure/auction/${encodeURIComponent(String(idAuction))}/file-message`,
			idFileMessage,
			{
				withCredentials: this.configuration.withCredentials,
				headers,
				observe,
				reportProgress
			}
		);
	}

	editAuctionMessageUsingPATCH = (auctionMessage: string, idAuction: number, observe?: "body", reportProgress?: boolean): Observable<any> =>
		this.httpClient.patch<any>(`${this.basePath}/api/v1/secure/auction/${encodeURIComponent(String(idAuction))}/auction-message`,
			auctionMessage,
			{
				withCredentials: this.configuration.withCredentials,
				headers: this.defaultHeaders,
				observe,
				reportProgress
			}
		);

	awardAuctionUsingPATCH = (idAuction: number, observe?: "body", reportProgress?: boolean): Observable<any> =>
		this.httpClient.patch<any>(`${this.basePath}/api/v1/secure/auction/${encodeURIComponent(String(idAuction))}/award`,
			null,
			{
				withCredentials: this.configuration.withCredentials,
				headers: this.defaultHeaders,
				observe,
				reportProgress
			}
		);

	editAuctionUsingPUT = (auctionDTO: AuctionFullDTO, observe?: "body", reportProgress?: boolean): Observable<any> =>
		this.httpClient.put<any>(`${this.basePath}/api/v1/secure/auction`,
			auctionDTO,
			{
				withCredentials: this.configuration.withCredentials,
				headers: this.defaultHeaders,
				observe,
				reportProgress
			}
		);

	getPortalInfoUsingGET(market: string, observe?: "body", reportProgress?: boolean): Observable<PortalInfoDTO> {
		let queryParameters = this.encoderParameters;
		if (market !== undefined && market !== null) {
			queryParameters = queryParameters.set("market", <any>market);
		}

		let headers = this.defaultHeaders;

		const httpHeaderAccepts: string[] = ["*/*"],
			httpHeaderAcceptSelected: string | undefined = this.configuration.selectHeaderAccept(httpHeaderAccepts);
		if (httpHeaderAcceptSelected != undefined) {
			headers = headers.set("Accept", httpHeaderAcceptSelected);
		}

		return this.httpClient.get<PortalInfoDTO>(`${this.basePath}/api/v1/open/info`,
			{
				params: queryParameters,
				withCredentials: this.configuration.withCredentials,
				headers,
				observe,
				reportProgress
			}
		);
	}


	getAuctionByIdTemplateUsingGET(idTemplate: number, observe?: "body", reportProgress?: boolean): Observable<AuctionDTO[]> {
		let headers = this.defaultHeaders;

		const httpHeaderAccepts: string[] = ["*/*"],
			httpHeaderAcceptSelected: string | undefined = this.configuration.selectHeaderAccept(httpHeaderAccepts);
		if (httpHeaderAcceptSelected != undefined) {
			headers = headers.set("Accept", httpHeaderAcceptSelected);
		}

		return this.httpClient.get<AuctionDTO[]>(`${this.basePath}/api/v1/secure/auction/${encodeURIComponent(String(idTemplate))}`,
			{
				withCredentials: this.configuration.withCredentials,
				headers,
				observe,
				reportProgress
			}
		);
	}

	getAuctionsUsingGET(ids?: number[], observe?: "body", reportProgress?: boolean): Observable<any> {
		let queryParameters = this.encoderParameters;
		if (ids) {
			for (const element of ids) {
				queryParameters = queryParameters.append("ids", <any>element);
			}
		}

		let headers = this.defaultHeaders;

		const httpHeaderAccepts: string[] = ["*/*"],
			httpHeaderAcceptSelected: string | undefined = this.configuration.selectHeaderAccept(httpHeaderAccepts);
		if (httpHeaderAcceptSelected != undefined) {
			headers = headers.set("Accept", httpHeaderAcceptSelected);
		}

		return this.httpClient.get<any>(`${this.basePath}/api/v1/secure/auction`,
			{
				params: queryParameters,
				withCredentials: this.configuration.withCredentials,
				headers,
				observe,
				reportProgress
			}
		);
	}

	getTimeUsingGET(observe?: "body", reportProgress?: boolean): Observable<number> {
		let headers = this.defaultHeaders;

		const httpHeaderAccepts: string[] = ["*/*"],
			httpHeaderAcceptSelected: string | undefined = this.configuration.selectHeaderAccept(httpHeaderAccepts);
		if (httpHeaderAcceptSelected != undefined) {
			headers = headers.set("Accept", httpHeaderAcceptSelected)
				.set(HeaderKey.NO_SPIN, "true");
		}

		return this.httpClient.get<number>(`${this.basePath}/api/v1/secure/time`,
			{
				withCredentials: this.configuration.withCredentials,
				headers,
				observe,
				reportProgress
			}
		);
	}

	// tslint:disable-next-line:max-line-length
	makeOfferUsingPOST(idAuction: number, idLot: number, offer: number, clickAndBuy: boolean, observe?: "body", reportProgress?: boolean): Observable<OfferResponseDTO> {
		let headers = this.defaultHeaders;

		const dto = {
			offer: offer,
			clickAndBuy: clickAndBuy
		},
			httpHeaderAccepts: string[] = ["*/*"],
			httpHeaderAcceptSelected: string | undefined = this.configuration.selectHeaderAccept(httpHeaderAccepts);
		if (httpHeaderAcceptSelected != undefined) {
			headers = headers.set("Accept", httpHeaderAcceptSelected)
				.set(HeaderKey.NO_SUCCESS_POPUP, "true");
		}

		// tslint:disable-next-line:max-line-length
		return this.httpClient.post<OfferResponseDTO>(`${this.basePath}/api/v1/secure/auction/${encodeURIComponent(String(idAuction))}/offer/${encodeURIComponent(String(idLot))}`,
			dto,
			{
				withCredentials: this.configuration.withCredentials,
				headers,
				observe,
				reportProgress
			}
		);
	}

	getLotBasePriceUsingGET(idLot: number, observe?: "body", reportProgress?: boolean): Observable<number> {
		let headers = this.defaultHeaders;

		const httpHeaderAccepts: string[] = ["*/*"],
			httpHeaderAcceptSelected: string | undefined = this.configuration.selectHeaderAccept(httpHeaderAccepts);
		if (httpHeaderAcceptSelected != undefined) {
			headers = headers
				.set(HeaderKey.NO_SPIN, "true")
				.set(HeaderKey.NO_POPUP, "true");
		}

		return this.httpClient.get<number>(`${this.basePath}/api/v1/secure/auction/${encodeURIComponent(String(idLot))}/base`,
			{
				withCredentials: this.configuration.withCredentials,
				headers,
				observe,
				reportProgress
			}
		);
	}

	revertAwardLotUsingPATCH = (idAuction: number, idLot: number, observe?: "body", reportProgress?: boolean): Observable<any> =>
		this.httpClient.patch<any>(`${this.basePath}/api/v1/secure/auction/${encodeURIComponent(String(idAuction))}/revert-award/${encodeURIComponent(String(idLot))}`,
			null,
			{
				withCredentials: this.configuration.withCredentials,
				headers: this.defaultHeaders,
				observe,
				reportProgress
			}
		);

	getRandomVehicleUsingGET(idAuctions: number[], observe?: "body", reportProgress?: boolean): Observable<{ [key: string]: VehicleDTO[]; }> {
		let queryParameters = this.encoderParameters;
		if (idAuctions) {
			for (const element of idAuctions) {
				queryParameters = queryParameters.append("idAuctions", <any>element);
			}
		}

		let headers = this.defaultHeaders;

		const httpHeaderAccepts: string[] = ["*/*"],
			httpHeaderAcceptSelected: string | undefined = this.configuration.selectHeaderAccept(httpHeaderAccepts);
		if (httpHeaderAcceptSelected != undefined) {
			headers = headers.set("Accept", httpHeaderAcceptSelected);
		}

		return this.httpClient.get<{ [key: string]: VehicleDTO[]; }>(`${this.basePath}/api/v1/secure/random-vehicle`,
			{
				params: queryParameters,
				withCredentials: this.configuration.withCredentials,
				headers,
				observe,
				reportProgress
			}
		);
	}

	observeAuctionLotUsingPOST(endObservation: boolean, idAuction: number, idLot: number, observe?: "body", reportProgress?: boolean): Observable<any> {
		let queryParameters = this.encoderParameters;
		if (endObservation !== undefined && endObservation !== null) {
			queryParameters = queryParameters.set("endObservation", <any>endObservation);
		}

		let headers = this.defaultHeaders;

		const httpHeaderAccepts: string[] = ["*/*"],
			httpHeaderAcceptSelected: string | undefined = this.configuration.selectHeaderAccept(httpHeaderAccepts);
		if (httpHeaderAcceptSelected != undefined) {
			headers = headers.set("Accept", httpHeaderAcceptSelected)
				.set(HeaderKey.NO_SUCCESS_POPUP, "true");
		}

		return this.httpClient.post<any>(`${this.basePath}/api/v1/secure/auction/${encodeURIComponent(String(idAuction))}/observe/${encodeURIComponent(String(idLot))}`,
			null,
			{
				params: queryParameters,
				withCredentials: this.configuration.withCredentials,
				headers,
				observe,
				reportProgress
			}
		);
	}


	getAuctionFullUsingGET(idAuction: number, observe?: "body", reportProgress?: boolean): Observable<AuctionFullDTO> {
		let queryParameters = this.encoderParameters;
		if (idAuction !== undefined && idAuction !== null) {
			queryParameters = queryParameters.set("idAuction", <any>idAuction);
		}

		return this.httpClient.get<AuctionFullDTO>(`${this.basePath}/api/v1/secure/auctionFull`,
			{
				params: queryParameters,
				withCredentials: this.configuration.withCredentials,
				headers: this.defaultHeaders,
				observe,
				reportProgress
			}
		);
	}

	getAuctionFullNoOfferUsingGET(idAuction: number, observe?: "body", reportProgress?: boolean): Observable<AuctionFullDTO> {
		let queryParameters = this.encoderParameters;
		if (idAuction !== undefined && idAuction !== null) {
			queryParameters = queryParameters.set("idAuction", <any>idAuction);
		}

		let headers = this.defaultHeaders;

		const httpHeaderAccepts: string[] = ["*/*"],
			httpHeaderAcceptSelected: string | undefined = this.configuration.selectHeaderAccept(httpHeaderAccepts);
		if (httpHeaderAcceptSelected != undefined) {
			headers = headers.set("Accept", httpHeaderAcceptSelected);
		}

		return this.httpClient.get<AuctionFullDTO>(`${this.basePath}/api/v1/secure/auctionFullNoOffer`,
			{
				params: queryParameters,
				withCredentials: this.configuration.withCredentials,
				headers,
				observe,
				reportProgress
			}
		);
	}

	acceptRegulationUsingPATCH = (idAuction: number, observe?: "body", reportProgress?: boolean): Observable<any> =>
		this.httpClient.patch<any>(`${this.basePath}/api/v1/secure/auction/${encodeURIComponent(String(idAuction))}/accept-regulation`,
			null,
			{
				withCredentials: this.configuration.withCredentials,
				headers: this.defaultHeaders,
				observe,
				reportProgress
			}
		);

	acceptedRegulationUsingGET = (idAuction: number, observe?: "body", reportProgress?: boolean): Observable<boolean> =>
		this.httpClient.get<boolean>(`${this.basePath}/api/v1/secure/auction/${encodeURIComponent(String(idAuction))}/accepted-regulation`,
			{
				withCredentials: this.configuration.withCredentials,
				headers: this.defaultHeaders,
				observe,
				reportProgress
			}
		);

	getPdfUsingGET(idPdf: number, filename: string, observe?: "body", reportProgress?: boolean): Observable<Blob> {
		let queryParameters = this.encoderParameters;
		if (idPdf !== undefined && idPdf !== null) {
			queryParameters = queryParameters.set("idPdf", <any>idPdf);
		}
		if (filename !== undefined && filename !== null) {
			queryParameters = queryParameters.set("filename", <any>filename);
		}

		const headers = this.defaultHeaders.set("Accept", "application/pdf"),
			requestOptions: object = {
				/* other options here */
				params: queryParameters,
				withCredentials: this.configuration.withCredentials,
				headers,
				observe,
				reportProgress,
				responseType: "blob"
			};

		return this.httpClient.get<Blob>(`${this.basePath}/api/v1/secure/pdf`,
			requestOptions
		);
	}

	/**
	 * getObservedAuctionLot
	 *
	 * @param idAuction idAuction
	 * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
	 * @param reportProgress flag to report request and response progress.
	 */
	getObservedAuctionLotUsingPOST(idAuction: number, observe?: "body", reportProgress?: boolean): Observable<ObservedAuctionDTO> {
		let headers = this.defaultHeaders;

		const httpHeaderAccepts: string[] = ["*/*"],
			httpHeaderAcceptSelected: string | undefined = this.configuration.selectHeaderAccept(httpHeaderAccepts);
		if (httpHeaderAcceptSelected != undefined) {
			headers = headers.set("Accept", httpHeaderAcceptSelected)
				.set(HeaderKey.NO_SUCCESS_POPUP, "true");
		}

		return this.httpClient.post<ObservedAuctionDTO>(`${this.basePath}/api/v1/secure/auction/${encodeURIComponent(String(idAuction))}/observed`,
			null,
			{
				withCredentials: this.configuration.withCredentials,
				headers,
				observe,
				reportProgress
			}
		);
	}

	getObservedAuctionsLotsUsingGET = (observe?: "body", reportProgress?: boolean): Observable<ObservedAuctionDTO[]> =>
		this.httpClient.get<ObservedAuctionDTO[]>(`${this.basePath}/api/v1/secure/auction/observed`,
			{
				withCredentials: this.configuration.withCredentials,
				headers: this.defaultHeaders,
				observe,
				reportProgress
			}
		);

	/**
	 * getImage
	 *
	 * @param idImage idImage
	 * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
	 * @param reportProgress flag to report request and response progress.
	 */
	getImageUsingGET(idImage: number, observe?: "body", reportProgress?: boolean): Observable<Blob> {
		let queryParameters = this.encoderParameters;
		if (idImage !== undefined && idImage !== null) {
			queryParameters = queryParameters.set("idImage", <any>idImage);
		}

		let headers = this.defaultHeaders;

		const httpHeaderAccepts: string[] = ["image/jpeg"],
			httpHeaderAcceptSelected: string | undefined = this.configuration.selectHeaderAccept(httpHeaderAccepts);
		if (httpHeaderAcceptSelected != undefined) {
			headers = headers.set("Accept", httpHeaderAcceptSelected);
		}

		return this.httpClient.get<Blob>(`${this.basePath}/api/v1/secure/image`,
			{
				params: queryParameters,
				withCredentials: this.configuration.withCredentials,
				headers,
				observe,
				reportProgress
			}
		);
	}

	getAuctionLotOffersReportUsingGET = (idAuction: number, observe?: "body", reportProgress?: boolean): Observable<LotReportDTO[]> =>
		this.httpClient.get<LotReportDTO[]>(`${this.k8sPath}/api/v1/secure/auction/${encodeURIComponent(String(idAuction))}/auction-offers-report`,
			{
				withCredentials: this.configuration.withCredentials,
				headers: this.defaultHeaders,
				observe,
				reportProgress
			}
		);

	notAwardAuctionLotUsingPOST = (idAuction: number, reports: LotReportDTO[], observe?: "body", reportProgress?: boolean): Observable<any> =>
		this.httpClient.post<any>(`${this.basePath}/api/v1/secure/auction/${encodeURIComponent(String(idAuction))}/not-award`,
			reports,
			{
				withCredentials: this.configuration.withCredentials,
				headers: this.defaultHeaders,
				observe,
				reportProgress
			}
		);

	createReportUsingGET(idAuction: number, typeExport: string, observe?: "body", reportProgress?: boolean): Observable<any> {
		let queryParameters = this.encoderParameters;
		queryParameters = queryParameters.set("typeExport", <any>typeExport);

		const headers = this.defaultHeaders.set("Accept", "text/csv; charset=utf-8"),
			requestOptions: any = {
				/* other options here */
				params: queryParameters,
				withCredentials: this.configuration.withCredentials,
				headers,
				observe,
				reportProgress,
				responseType: "arraybuffer"
			};

		return this.httpClient.get<any>(`${this.basePath}/api/v1/secure/auction/${encodeURIComponent(String(idAuction))}/create-report`,
			requestOptions
		);
	}

	getPurchasesUsingGET(auctionName?: string, channel?: ChannelType, fromDate?: Date, idVehicle?: number, frame?: string, licensePlate?: string, toDate?: Date, observe?: "body", reportProgress?: boolean): Observable<Purchase[]> {
		let queryParameters = this.encoderParameters;
		if (auctionName !== undefined && auctionName !== null) {
			queryParameters = queryParameters.set("auctionName", <any>auctionName);
		}
		if (channel !== undefined && channel !== null) {
			queryParameters = queryParameters.set("channel", <any>channel);
		}
		if (fromDate !== undefined && fromDate !== null) {
			queryParameters = queryParameters.set("fromDate", <any>fromDate);
		}
		if (idVehicle !== undefined && idVehicle !== null) {
			queryParameters = queryParameters.set("idVehicle", <any>idVehicle);
		}
		if (licensePlate !== undefined && licensePlate !== null) {
			queryParameters = queryParameters.set("licensePlate", <any>licensePlate);
		}
		if (toDate !== undefined && toDate !== null) {
			queryParameters = queryParameters.set("toDate", <any>toDate);
		}
		if (frame !== undefined && frame !== null) {
			queryParameters = queryParameters.set("frame", <any>frame);
		}

		return this.httpClient.get<Purchase[]>(`${this.basePath}/api/v1/secure/purchases`,
			{
				params: queryParameters,
				withCredentials: this.configuration.withCredentials,
				headers: this.defaultHeaders,
				observe,
				reportProgress
			}
		);
	}

	deleteAuctionUsingDELETE = (idAuction: number, observe?: "body", reportProgress?: boolean): Observable<any> =>
		this.httpClient.delete<any>(`${this.basePath}/api/v1/secure/auction/${encodeURIComponent(String(idAuction))}/`,
			{
				withCredentials: this.configuration.withCredentials,
				headers: this.defaultHeaders,
				observe,
				reportProgress
			}
		);

	resizeEndTimeUsingPATCH = (auctionDTO: AuctionDTO, idAuction: number, observe?: "body", reportProgress?: boolean): Observable<any> =>
		this.httpClient.patch<any>(`${this.basePath}/api/v1/secure/auction/${encodeURIComponent(String(idAuction))}/resize-time`,
			auctionDTO,
			{
				withCredentials: this.configuration.withCredentials,
				headers: this.defaultHeaders,
				observe,
				reportProgress
			}
		);

	auctionFeedbackPOST = (feedback: FeedbackDTO, idAuction: number, observe?: "body", reportProgress?: boolean): Observable<any> =>
		this.httpClient.post<any>(`${this.basePath}/api/v1/secure/auction/${encodeURIComponent(String(idAuction))}/feedback`,
			feedback,
			{
				withCredentials: this.configuration.withCredentials,
				headers: this.defaultHeaders,
				observe,
				reportProgress
			}
		);

	getRunningAuctionsUsingGET(observe?: "body", reportProgress?: boolean): Observable<AuctionDTO[]> {
		let headers = this.defaultHeaders;

		const httpHeaderAccepts: string[] = ["*/*"],
			httpHeaderAcceptSelected: string | undefined = this.configuration.selectHeaderAccept(httpHeaderAccepts);
		if (httpHeaderAcceptSelected != undefined) {
			headers = headers.set("Accept", httpHeaderAcceptSelected)
				.set(HeaderKey.NO_POPUP, "true");
		}

		return this.httpClient.get<AuctionDTO[]>(`${this.basePath}/api/v1/secure/running-auctions`,
			{
				withCredentials: this.configuration.withCredentials,
				headers,
				observe,
				reportProgress
			}
		);
	}

	getLotDetailsUsingGET = (idAuction: number, idLot: number, observe?: "body", reportProgress?: boolean): Observable<LotDetailDTO> =>
		this.httpClient.get<LotDetailDTO>(`${this.basePath}/api/v1/secure/auction/${encodeURIComponent(String(idAuction))}/lot-detail/${encodeURIComponent(String(idLot))}`,
			{
				withCredentials: this.configuration.withCredentials,
				headers: this.defaultHeaders,
				observe,
				reportProgress
			}
		);

	deleteLotOfferUsingDELETE = (idAuction: number, idLot: number, idOffer: number, observe?: "body", reportProgress?: boolean): Observable<any> =>
		this.httpClient.delete<any>(`${this.basePath}/api/v1/secure/auction/${encodeURIComponent(String(idAuction))}/lot-detail/${encodeURIComponent(String(idLot))}/${encodeURIComponent(String(idOffer))}`,
			{
				withCredentials: this.configuration.withCredentials,
				headers: this.defaultHeaders,
				observe,
				reportProgress
			}
		);

	deleteFileMessageUsingDELETE = (idAuction: number, observe?: "body", reportProgress?: boolean): Observable<any> =>
		this.httpClient.delete<any>(`${this.basePath}/api/v1/secure/auction/${idAuction}/file-message`,
			{
				withCredentials: this.configuration.withCredentials,
				headers: this.defaultHeaders,
				observe,
				reportProgress
			}
		);
}